import Mention, { MentionOptions } from '@tiptap/extension-mention';
import { MarkdownNodeSpec } from 'tiptap-markdown';

import { SuggestionCandidate } from './typings';
import { Token } from 'markdown-it';

/**
 *
 * @param {String} mentionTrigger
 * @param {bool} allowSpace
 * @returns {Object}
 */
export function getMentionRegexp(mentionTrigger: string) {
  const mention = new RegExp(
    '(?:^|\\s)(' + mentionTrigger + '(\\w+(?:\\s\\w+)?))(?:[.!?:;\\s]|$)',
    'g',
  );

  return mention;
}

type ExtendedMentionOptions = MentionOptions & {
  char: string;
  items: SuggestionCandidate[];
  parseKnownOnly?: boolean;
};

type ExtendedMentionStorage = { markdown: MarkdownNodeSpec } & {
  mention: {
    char: string;
    items: SuggestionCandidate[];
    praseKnownOnly: boolean;
  };
};

export default Mention.extend<ExtendedMentionOptions>({
  addOptions() {
    return {
      ...this.parent?.(),
      char: '@',
      items: [],
      parseKnownOnly: true,
    };
  },
  addStorage(): ExtendedMentionStorage {
    return {
      ...this.parent?.(),
      mention: {
        char: this.options.char,
        items: this.options.items,
        parseKnownOnly: this.options.parseKnownOnly,
      },
      markdown: {
        serialize(state, node) {
          state.text(`${this.options.suggestion.char}${node.attrs.label ?? node.attrs.id}`); // TODO: @Lajos We are serializing the label only, so it doesn't matter now the ID
        },
        parse: {
          // handled by markdown-it
          setup(markdownIt) {
            const className = this.options.HTMLAttributes.class;
            const char = this.options.suggestion.char;
            const parseKnownOnly = this.options.parseKnownOnly;
            markdownIt.use(md => {
              md.renderer.rules.mention = (tokens, idx) => {
                return `<span class="${className}" data-type="mention" data-id="${tokens[idx].content}">${char}${tokens[idx].content}</span>`;
              };

              md.core.ruler.after('inline', 'mention', state => {
                const matcher = getMentionRegexp(char);

                state.tokens.forEach(blockToken => {
                  if (blockToken.type !== 'inline') return;

                  const { children } = blockToken;

                  const newChildren: Token[] =
                    children?.flatMap(token => {
                      if (token.type !== 'text') return [token];
                      // NOTE: Match all mention-like in text
                      const matches = [...token.content.matchAll(matcher)];
                      if (!matches.length) return [token];

                      let lastEnd = 0;
                      const result: Token[] = [];

                      for (const match of matches) {
                        const start = match.index! + (match[0].startsWith(' ') ? 1 : 0);
                        const end = match.index! + match[0].length;

                        // NOTE: Push the text before the mention
                        if (start > lastEnd) {
                          result.push(
                            Object.assign({}, token, {
                              type: 'text',
                              content: token.content.substring(lastEnd, start),
                            }),
                          );
                        }
                        // NOTE: If we only parse mentions that are known from the items array
                        if (parseKnownOnly) {
                          // NOTE: Find the known mention by matching the start of the matched string(without the initiator char).
                          // Eg. @Response 1asd will be @Response 1 and asd
                          const suggestion = this.options.items.find(
                            (item: SuggestionCandidate) =>
                              match[2].startsWith(item.id) || match[2].startsWith(item.label),
                          );
                          if (suggestion) {
                            // NOTE: If we found the suggestion, push it as a mention
                            result.push(
                              Object.assign({}, token, {
                                type: 'mention',
                                content: suggestion?.label,
                              }),
                            );
                            // NOTE: Check if we have any remaining chars after the known mention and push it as a text
                            const remainingChars = match[0]
                              .trim()
                              .replace(char, '')
                              .replace(suggestion.label, '');
                            if (remainingChars.length) {
                              result.push(
                                Object.assign({}, token, {
                                  type: 'text',
                                  content: remainingChars,
                                }),
                              );
                            }
                          } else {
                            // NOTE: If we only want known mentions push the found mention as normal text
                            result.push(
                              Object.assign({}, token, { type: 'text', content: match[1] }),
                            );
                          }
                          // NOTE: If we have an extra space after the mention keep it
                          if (match[0].endsWith(' ')) {
                            result.push(
                              Object.assign({}, token, {
                                type: 'text',
                                content: ' ',
                              }),
                            );
                          }
                        } else {
                          // NOTE: If we want not known mentions as well, push the mention as a mention
                          result.push(
                            Object.assign({}, token, {
                              type: 'mention',
                              content: match[2].trim(),
                            }),
                          );
                        }

                        lastEnd = end;
                      }

                      if (lastEnd < token.content.length) {
                        result.push(
                          Object.assign({}, token, {
                            type: 'text',
                            content: token.content.substring(lastEnd),
                          }),
                        );
                      }

                      return result;
                    }) ?? [];

                  blockToken.children = newChildren;
                });
              });
            });
          },
        },
      },
    };
  },
});
